<template>
  <footer class="the-footer">
    <div class="inner container">
      <div class="px-2">
        <NuxtLink
          class="logo"
          :class="
            footerConfig.public.footer.logoHeightSize
              ? footerConfig.public.footer.logoHeightSize
              : 'h-12'
          "
          :to="localePath('/')"
        >
          <NuxtImg
            loading="lazy"
            v-no-right-click
            :src="companyInfo.logo"
            alt="logo"
          />
        </NuxtLink>
        <p class="description">
          {{ t(companyInfo.about_company) }}
        </p>
        <div
          class="mb-14 md:mb-10"
          v-if="
            companyInfo.social_medias &&
            Object.entries(companyInfo.social_medias)
          "
        >
          <span class="company-link-title">{{ t("footer.follow") + ":" }}</span>
          <TheFooterSocialMedias :socials="companyInfo.social_medias" />
        </div>
        <div
          v-if="!companyInfo.hideAcceptedPayment"
          class="hidden md:inline-block"
        >
          <span class="company-link-title">
            {{ t("footer.payment") + ":" }}
          </span>
          <div
            v-if="
              companyInfo.acceptedPayments &&
              companyInfo.acceptedPayments.length > 0
            "
            class="flex gap-1.5"
          >
            <NuxtImg
              loading="lazy"
              v-for="acceptedPayment in companyInfo.acceptedPayments"
              :key="acceptedPayment"
              v-no-right-click
              :src="`/${acceptedPayment}.svg`"
              :alt="`${acceptedPayment} payment`"
              class="h-8 w-12"
            />
          </div>
          <NuxtImg
            loading="lazy"
            v-else
            v-no-right-click
            src="https://tw028jd0.turpal.com/assets/accept-payment.png"
            alt="accept payment"
            class="h-8"
          />
        </div>
      </div>
      <NuxtImg
        v-if="companyInfo.license"
        :src="companyInfo.license"
        class="w-[200px] rounded-[5px] mb-5 md:hidden"
        v-no-right-click
        loading="lazy"
      />

      <a
        v-if="companyInfo.enamad"
        referrerpolicy="origin"
        target="_blank"
        :href="companyInfo.enamad?.href"
        class="w-[200px] rounded-[5px] mb-5 md:hidden"
      >
        <NuxtImg
          referrerpolicy="origin"
          :src="companyInfo.enamad?.src"
          alt=""
          style="cursor: pointer"
          :code="companyInfo.enamad?.code"
        />
      </a>
      <div class="links-list" :class="{ active: activeList === 1 }">
        <h6 class="title" @click="toggleList(1)">
          <span>{{ companyInfo.name }}</span>
          <icon-chevron />
        </h6>
        <ul>
          <li
            v-for="(item, index) in footerConfig.public.footer.items"
            :key="index"
            class="link-item"
          >
            <NuxtLink :to="localePath(item.target)">
              {{ t(item?.title) }}
            </NuxtLink>
          </li>
        </ul>
        <NuxtImg
          v-if="companyInfo.license"
          :src="companyInfo.license"
          class="w-[200px] rounded-[5px] mt-5 max-md:hidden"
          loading="lazy"
        />

        <a
          v-if="companyInfo.enamad"
          referrerpolicy="origin"
          target="_blank"
          :href="companyInfo.enamad?.href"
          class="block w-[200px] rounded-[5px] mb-5 max-md:hidden"
        >
          <NuxtImg
            referrerpolicy="origin"
            :src="companyInfo.enamad?.src"
            alt=""
            style="cursor: pointer"
            :code="companyInfo.enamad?.code"
          />
        </a>
      </div>
      <div
        v-if="
          config.public.showCategories && !config.public.footer.hideCategories
        "
        class="links-list"
        :class="{ active: activeList === 2 }"
      >
        <h6 class="title" @click="toggleList(2)">
          <span>{{ t("categories") }}</span>
          <icon-chevron />
        </h6>
        <ul>
          <li
            v-for="category in categories?.slice(0, 10)"
            :key="category.id"
            class="link-item"
          >
            <NuxtLink :to="categoryPath(category.slug)">
              {{ category?.title }}
            </NuxtLink>
          </li>
          <li v-if="categories && categories.length > 10" class="link-item">
            <NuxtLink :to="localePath('/things-to-do')">
              {{ "+" + t("footer.showMore") }}
            </NuxtLink>
          </li>
        </ul>
      </div>
      <div class="links-list" :class="{ active: activeList === 3 }">
        <h6 class="title" @click="toggleList(3)">
          <span>{{ t("footer.cities") }}</span>
          <icon-chevron />
        </h6>
        <ul>
          <li
            v-for="city in cities?.slice(0, 10)"
            :key="city.city.id"
            class="link-item"
          >
            <NuxtLink :to="localePath(`/things-to-do-in-${city.city.slug}`)">
              {{ city.city.name }}
            </NuxtLink>
          </li>
          <li v-if="cities && cities.length > 10" class="link-item">
            <NuxtLink :to="localePath('/things-to-do')">
              {{ "+" + t("footer.showMore") }}
            </NuxtLink>
          </li>
        </ul>
      </div>
      <div v-if="$te('footer.commercialRegistrationLabel')">
        <div class="">
          <h6 class="text-heading-6 mb-1">
            {{ t("footer.emailLabel") }}
          </h6>
          <div
            class="text-mobile-heading-5 lg:text-heading-5 mb-5 text-footer-text-color"
          >
            {{ companyInfo.email }}
          </div>
        </div>
        <div class="">
          <h6 class="text-heading-6 mb-1">
            {{ t("footer.commercialRegistrationLabel") }}
          </h6>
          <div
            class="text-mobile-heading-5 lg:text-heading-5 mb-5 text-footer-text-color"
          >
            {{ t("footer.commercialRegistrationValue") }}
          </div>
        </div>
        <div class="">
          <h6 class="text-heading-6 mb-1">
            {{ t("footer.licenseNumberLabel") }}
          </h6>
          <div
            class="text-mobile-heading-5 lg:text-heading-5 mb-5 text-footer-text-color"
          >
            {{ t("footer.licenseNumberValue") }}
          </div>
        </div>
        <div class="">
          <h6 class="text-heading-6 mb-1">
            {{ t("footer.taxNumberLabel") }}
          </h6>
          <div
            class="text-mobile-heading-5 lg:text-heading-5 mb-5 text-footer-text-color"
          >
            {{ t("footer.taxNumberValue") }}
          </div>
        </div>
      </div>
      <div
        v-if="$te('footer.secondBranchAddressValue')"
        class="md:max-w-[240px]"
      >
        <h6 class="text-heading-6 mb-1">
          {{ t("footer.secondBranchAddressLabel") }}:
        </h6>
        <div
          class="text-mobile-heading-5 lg:text-heading-5 mb-5 text-footer-text-color"
        >
          {{ t("footer.secondBranchAddressValue") }}
        </div>
      </div>
      <div v-if="!companyInfo.hideAcceptedPayment" class="md:hidden mt-10">
        <span class="company-link-title">
          {{ t("footer.payment") + ":" }}
        </span>
        <div
          v-if="
            companyInfo.acceptedPayments &&
            companyInfo.acceptedPayments.length > 0
          "
          class="flex gap-1.5"
        >
          <NuxtImg
            loading="lazy"
            v-for="acceptedPayment in companyInfo.acceptedPayments"
            :key="acceptedPayment"
            v-no-right-click
            :src="`/${acceptedPayment}.svg`"
            :alt="`${acceptedPayment} payment`"
            class="h-8 w-12"
          />
        </div>
        <NuxtImg
          loading="lazy"
          v-else
          v-no-right-click
          src="https://tw028jd0.turpal.com/assets/accept-payment.png"
          alt="accept payment"
          class="h-8"
        />
      </div>
    </div>
    <div v-if="$te('rights')" class="container">
      <div class="rights">
        {{ t("rightsReserved") }} {{ companyInfo.name }} @{{ t("rightsYear") }}
      </div>
    </div>
  </footer>
</template>
<script setup lang="ts">
import { ref, watch } from "vue";
import customFetch from "../../../composables/customFetch";
import type { ITopCities, ICategory, IApiResponse } from "../../../types/general";
import TheFooterSocialMedias from "./TheFooterSocialMedias.vue";
import useCustomFetch from "../../../composables/useCustomFetch";
import { useAsyncData } from "nuxt/app";

const localePath = useLocalePath();
const { categoryPath } = usePath();
const config = useRuntimeConfig() as unknown as RuntimeConfiguration;
const { t, locale } = useI18n();

const footerConfig = ref<RuntimeConfiguration>(config);
const { fetchBlog, blogs } = useBlog();
const { data: blogsData } = await useAsyncData("footer-blogs", async () => {
  await fetchBlog();
  return blogs.value;
});
if (!blogsData.value?.length) {
  footerConfig.value.public.footer.items =
    footerConfig.value.public.footer.items?.filter(
      (item) => item.target !== "/blog"
    );
}
const [{ data: cities }, { data: categories }] = await Promise.all([
  useAsyncData<ITopCities[] | null>("top-cities", () =>
    customFetch(`api/top-cities`)
  ),
  useAsyncData<ICategory[] | null>("categories", () =>
    customFetch(`api/categories`)
  ),
]);
const companyInfo = {
  name: footerConfig.value.public.name,
  about_company: footerConfig.value.public.footer.text,
  email: footerConfig.value.public.email,
  logo: footerConfig.value.public.footer.logo,
  social_medias: footerConfig.value.public.footer.socialLinks,
  license: footerConfig.value.public.footer?.license?.img ?? null,
  hideAcceptedPayment:
    footerConfig.value.public.footer?.hideAcceptedPayment ?? false,
  acceptedPayments: footerConfig.value.public.footer?.acceptedPayments ?? [],
  enamad: footerConfig.value.public.footer?.enamad ?? null,
};
const activeList = ref<number>(0);
const activeLocale = locale;

watch(activeLocale, () => {
  updateCitiesList();
  updateCategoriesList();
});

const toggleList = (id: number) => {
  activeList.value = activeList.value === id ? 0 : id;
};

async function updateCitiesList() {
  const { data, fetchData: updateCities } = useCustomFetch(`/api/top-cities`);
  await updateCities();
  const citiesTmp: IApiResponse<ITopCities[]> | null =
    data.value as IApiResponse<ITopCities[]> | null;

  cities.value = citiesTmp?.data ?? [];
}

async function updateCategoriesList() {
  const { data, fetchData: updateCategories } =
    useCustomFetch(`/api/categories`);
  await updateCategories();
  const categoriesTmp: IApiResponse<ICategory[]> | null =
    data.value as IApiResponse<ICategory[]> | null;

  categories.value = categoriesTmp?.data ?? [];
}
</script>
<style scoped>
.the-footer {
  @apply bg-footer-background;
}

.the-footer .inner {
  @apply pt-10 pb-8 md:pb-11
  flex flex-col md:flex-row justify-between items-start
  text-footer-text-color;
}
.the-footer .inner .logo {
  @apply inline-flex max-md:!h-12 w-auto object-cover mb-2.5;
}
.the-footer .inner .logo img {
  @apply rounded-none;
}
.the-footer .inner .description {
  @apply text-mobile-heading-5 lg:text-heading-5 md:max-w-[17rem] mb-10 md:mb-12
   text-footer-text-color;
}

.the-footer .inner .links-list {
  @apply md:px-2 xs:max-md:w-full
    xs:max-md:border-b xs:max-md:border-gray-2
    transition-all duration-500 ease-in-out;
}

.the-footer .inner .links-list .title {
  @apply md:mb-5 py-5 md:py-0 text-footer-text-color
 flex justify-between items-center
 xs:max-md:cursor-pointer;
}

.the-footer .inner .links-list .title svg {
  @apply md:hidden rotate-90
  mb-0 transition-transform duration-500;
}
.the-footer .inner .links-list .title span {
  @apply text-heading-6;
}
.the-footer .inner .links-list.active .title svg {
  @apply md:hidden -rotate-90;
}
.the-footer .inner .links-list ul {
  @apply max-h-0 overflow-hidden md:max-h-full
    transition-[max-height] ease-in-out duration-[400ms];
}
.the-footer .inner .links-list.active ul {
  @apply max-h-[500px];
}

.the-footer .inner .links-list .link-item {
  @apply text-mobile-heading-5 lg:text-heading-5 mb-5 text-footer-text-color;
}

.the-footer .inner .company-link-title {
  @apply block text-mobile-paragraph-2 lg:text-paragraph-2 text-footer-text-color mb-1;
}

.the-footer .rights {
  @apply text-footer-text-color text-paragraph-2 pb-10 md:pb-5;
}
</style>
